import api from './apiService';

/**
 * Server-backed chat so member messages reach every admin.
 * Polling + custom events keep UI in sync across browsers.
 */
class ChatService {
  notify(type, detail) {
    try {
      window.dispatchEvent(new CustomEvent(type, { detail }));
    } catch (_) { /* ignore */ }
  }

  async getUnreadMessages() {
    const { data } = await api.get('/chat/unread');
    return Array.isArray(data) ? data : [];
  }

  async getUnreadCountsByMember() {
    const { data } = await api.get('/chat/unread-counts');
    return data && typeof data === 'object' ? data : {};
  }

  async getUnreadCount() {
    const unread = await this.getUnreadMessages();
    return unread.length;
  }

  async getMemberChats(memberId) {
    const { data } = await api.get(`/chat/member/${memberId}`);
    return Array.isArray(data) ? data : [];
  }

  async getMemberChatHistory(memberId) {
    return this.getMemberChats(memberId);
  }

  async addMemberMessage(memberId, memberName, memberUan, message) {
    const { data } = await api.post('/chat/messages', {
      member_id: memberId,
      member_name: memberName,
      member_uan: memberUan,
      message,
      sender: 'member'
    });
    this.notify('newChatMessage', data);
    return data;
  }

  /**
   * Admin reply as a new thread message (visible to member + all admins).
   * repliedByName shows as "Replied by: …"
   */
  async addAdminReply(memberId, reply, { adminName, adminUserId } = {}) {
    const { data } = await api.post('/chat/messages', {
      member_id: memberId,
      message: reply,
      sender: 'admin',
      admin_name: adminName || 'Admin',
      admin_user_id: adminUserId || null
    });
    this.notify('adminReplyAdded', data);
    this.notify('newChatMessage', data);
    return data;
  }

  async markAsRead(messageId) {
    if (!messageId) return null;
    await api.put(`/chat/messages/${messageId}/read`);
    this.notify('chatReadUpdated', { messageId });
    return true;
  }

  async markMemberRead(memberId) {
    if (!memberId) return null;
    await api.put(`/chat/member/${memberId}/read`);
    this.notify('chatReadUpdated', { memberId });
    return true;
  }

  async markAllAsRead() {
    const unread = await this.getUnreadMessages();
    const memberIds = [...new Set(unread.map((m) => m.member_id))];
    await Promise.all(memberIds.map((id) => this.markMemberRead(id)));
    return true;
  }
}

export default new ChatService();
